import type { LegalDocument, LegalLanguage } from './legal-document.ts'

/** Change this in one place and both languages follow. */
export const CONTACT_EMAIL = 'support@ytclip.org'

/** Bump whenever a clause changes — it is what the page shows the reader. */
const EFFECTIVE_DATE = '2026-09-22'

const ID: LegalDocument = {
  title: 'Kebijakan Privasi',
  subtitle:
    'Apa yang kami kumpulkan saat kamu memakai YT Clip AI, kenapa kami menyimpannya, dan apa yang bisa kamu lakukan terhadap data itu.',
  effectiveDate: EFFECTIVE_DATE,
  effectiveLabel: 'Berlaku sejak',
  summary:
    'Singkatnya: kami menyimpan identitas dasar dari akun Google kamu, catatan top-up, dan metadata pemakaian (model, jumlah token, durasi, biaya). Kami tidak menyimpan isi prompt maupun jawaban model di sistem kami, dan kami tidak pernah menerima data kartu atau PIN pembayaranmu.',
  tocLabel: 'Isi kebijakan',
  sections: [
    {
      id: 'ruang-lingkup',
      heading: 'Ruang lingkup',
      blocks: [
        {
          kind: 'p',
          text: 'Kebijakan ini berlaku untuk situs YT Clip AI, dashboard-nya, API yang kami sediakan, dan aplikasi desktop yang terhubung ke layanan ini. Dengan memakai salah satunya, kamu menyetujui cara pengelolaan data yang dijelaskan di bawah.',
        },
        {
          kind: 'p',
          text: 'YT Clip AI adalah layanan kredit AI: kamu mengisi saldo, lalu memakainya untuk memanggil model AI melalui API kami. Layanan ini dioperasikan dari Indonesia dan tunduk pada hukum Republik Indonesia.',
        },
      ],
    },
    {
      id: 'data-yang-dikumpulkan',
      heading: 'Data yang kami kumpulkan',
      blocks: [
        {
          kind: 'p',
          text: 'Kami hanya mengumpulkan data yang dibutuhkan agar layanan bisa berjalan dan bisa dipertanggungjawabkan secara keuangan:',
        },
        {
          kind: 'ul',
          items: [
            'Data akun — nama, alamat email, dan foto profil yang diberikan Google saat kamu login. Login hanya lewat Google; kami tidak pernah menerima atau menyimpan kata sandi Google kamu.',
            'Data transaksi — nominal top-up dalam USD dan IDR, biaya platform, kurs yang dipakai, status pembayaran, waktu, dan nomor referensi dari penyedia pembayaran. Kami tidak menerima dan tidak menyimpan nomor kartu, PIN, OTP, maupun kredensial mobile banking atau e-wallet kamu — semua itu ditangani langsung oleh penyedia pembayaran.',
            'Metadata pemakaian — untuk setiap permintaan ke model AI: nama model, jumlah token masukan dan keluaran, durasi, status berhasil atau gagal, pesan kesalahan bila ada, dan biaya yang dipotong.',
            'Data API key — nama key yang kamu beri, batas belanja yang kamu tetapkan, waktu pembuatan, dan status aktif atau dicabut.',
            'Data teknis — alamat IP, jenis peramban, dan log server yang tercatat otomatis saat kamu mengakses layanan, dipakai untuk keamanan dan penelusuran gangguan.',
          ],
        },
      ],
    },
    {
      id: 'prompt-dan-jawaban',
      heading: 'Isi prompt dan jawaban model',
      blocks: [
        {
          kind: 'p',
          text: 'Catatan pemakaian yang kami simpan berisi metadata saja — model, token, durasi, biaya. Isi prompt yang kamu kirim dan jawaban yang dikembalikan model tidak kami simpan di basis data YT Clip AI.',
        },
        {
          kind: 'p',
          text: 'Namun, isi itu tetap harus diteruskan ke penyedia model yang kamu pilih agar permintaanmu bisa diproses. Apa yang penyedia model lakukan terhadap data tersebut tunduk pada kebijakan privasi mereka masing-masing, bukan kebijakan ini. Jangan mengirimkan data pribadi sensitif, rahasia perusahaan, atau kredensial melalui prompt.',
        },
      ],
    },
    {
      id: 'cara-kami-memakai',
      heading: 'Cara kami memakai data',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Menyediakan layanan — membuat akun, menerbitkan API key, meneruskan permintaanmu ke model, dan memotong saldo sesuai pemakaian.',
            'Memproses pembayaran — membuat kode QRIS, mencocokkan pembayaran yang masuk, dan menambahkan saldo ke akunmu.',
            'Menampilkan riwayat — agar kamu bisa memeriksa sendiri setiap request dan setiap top-up beserta biayanya.',
            'Keamanan dan pencegahan penyalahgunaan — mendeteksi penyalahgunaan, aktivitas mencurigakan, atau pelanggaran syarat layanan.',
            'Dukungan pengguna — menjawab pertanyaan dan menelusuri masalah yang kamu laporkan.',
            'Kewajiban hukum — memenuhi ketentuan perpajakan, akuntansi, dan permintaan sah dari otoritas berwenang.',
          ],
        },
        {
          kind: 'p',
          text: 'Kami tidak menjual data pribadimu, dan kami tidak memakai datamu untuk iklan bertarget.',
        },
      ],
    },
    {
      id: 'pihak-ketiga',
      heading: 'Pihak ketiga yang memproses data',
      blocks: [
        {
          kind: 'p',
          text: 'Agar layanan bisa berjalan, sebagian data dibagikan seperlunya kepada penyedia berikut:',
        },
        {
          kind: 'ul',
          items: [
            'Google — untuk autentikasi. Google memberi kami nama, email, dan foto profilmu setelah kamu memberi izin.',
            'Penyedia pembayaran QRIS — untuk menerbitkan kode pembayaran dan mengonfirmasi pembayaran yang masuk. Mereka menerima nominal dan referensi transaksi.',
            'Penyedia model AI — untuk memproses setiap permintaan yang kamu kirim, termasuk isi prompt-nya.',
            'Penyedia infrastruktur dan hosting — untuk menjalankan server, basis data, dan pengiriman konten situs.',
            'Penyedia email dan Telegram — untuk notifikasi layanan dan, bagi member Gold, akses grup dukungan VIP.',
          ],
        },
        {
          kind: 'p',
          text: 'Sebagian penyedia ini berada di luar Indonesia, sehingga data dapat diproses lintas negara. Kami juga dapat mengungkapkan data bila diwajibkan oleh hukum atau untuk melindungi hak dan keamanan pengguna lain.',
        },
      ],
    },
    {
      id: 'cookie',
      heading: 'Cookie dan penyimpanan lokal',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Cookie sesi — satu cookie httpOnly yang menandakan kamu sudah login. Tanpa cookie ini dashboard tidak bisa dipakai, jadi sifatnya wajib dan bukan untuk pelacakan.',
            'Penyimpanan lokal peramban — menyimpan preferensi tampilan seperti pilihan bahasa di halaman ini. Datanya tidak pernah dikirim ke server kami.',
          ],
        },
        {
          kind: 'p',
          text: 'Kami tidak memasang cookie iklan maupun pelacak pihak ketiga untuk profiling.',
        },
      ],
    },
    {
      id: 'aplikasi-desktop',
      heading: 'Aplikasi desktop dan token perangkat',
      blocks: [
        {
          kind: 'p',
          text: 'Aplikasi desktop bisa dipakai tanpa registrasi. Saat pertama dijalankan, aplikasi mendaftarkan dirinya dan menerima satu token perangkat; yang kami simpan hanyalah identitas perangkat itu, platform sistem operasinya, versi aplikasi, saldo, dan riwayat pemakaiannya — tanpa nama dan tanpa email.',
        },
        {
          kind: 'p',
          text: 'Token itu hanya ditampilkan satu kali dan tidak bisa diambil ulang. Karena tidak ada akun yang bisa dipulihkan, token yang hilang berarti saldo yang hilang. Simpan token itu baik-baik dan jangan bagikan ke siapa pun.',
        },
      ],
    },
    {
      id: 'keamanan',
      heading: 'Keamanan',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Semua lalu lintas ke layanan berjalan lewat HTTPS.',
            'Sesi login disimpan dalam cookie httpOnly sehingga tidak bisa dibaca oleh skrip di peramban.',
            'Kredensial integrasi disimpan dalam keadaan terenkripsi dan tidak pernah dikirimkan kembali ke peramban.',
            'Secret API key hanya ditampilkan satu kali saat dibuat; setelah itu kami hanya menyimpan bentuk yang tidak bisa dikembalikan ke aslinya.',
          ],
        },
        {
          kind: 'p',
          text: 'Tidak ada sistem yang sepenuhnya kebal. Bila kamu menduga akunmu disalahgunakan, segera cabut API key kamu dari dashboard dan hubungi kami.',
        },
      ],
    },
    {
      id: 'retensi',
      heading: 'Berapa lama data disimpan',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Data akun disimpan selama akunmu aktif.',
            'Catatan transaksi dan pemakaian disimpan selama masih dibutuhkan untuk keperluan pembukuan, audit, dan kewajiban hukum, meskipun akun sudah ditutup.',
            'Log teknis disimpan dalam jangka pendek untuk keperluan keamanan dan penelusuran gangguan.',
          ],
        },
      ],
    },
    {
      id: 'hak-kamu',
      heading: 'Hak kamu atas data',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Melihat data akun, riwayat transaksi, dan riwayat pemakaianmu langsung dari dashboard kapan saja.',
            'Meminta koreksi data yang tidak akurat.',
            'Meminta penghapusan akun. Catatan keuangan yang wajib kami simpan menurut hukum akan tetap disimpan, dan saldo yang tersisa hangus sesuai Syarat & Ketentuan.',
            'Menarik persetujuan dengan berhenti memakai layanan dan mencabut izin aplikasi di pengaturan akun Google kamu.',
          ],
        },
        {
          kind: 'p',
          text: `Kirim permintaan tersebut ke ${CONTACT_EMAIL} dari alamat email yang terdaftar di akunmu.`,
        },
      ],
    },
    {
      id: 'usia',
      heading: 'Batas usia',
      blocks: [
        {
          kind: 'p',
          text: 'Layanan ini tidak ditujukan untuk anak di bawah 18 tahun. Bila kamu belum berusia 18 tahun, pemakaian hanya boleh dilakukan dengan persetujuan dan pengawasan orang tua atau wali. Bila kami mengetahui adanya akun anak yang dibuat tanpa persetujuan itu, akun tersebut akan dihapus.',
        },
      ],
    },
    {
      id: 'perubahan',
      heading: 'Perubahan kebijakan',
      blocks: [
        {
          kind: 'p',
          text: 'Kebijakan ini dapat kami perbarui sewaktu-waktu. Tanggal berlaku di bagian atas halaman selalu menunjukkan versi terbaru. Bila ada perubahan yang berdampak besar, kami akan memberitahu lewat email atau pengumuman di dashboard. Melanjutkan pemakaian setelah perubahan berlaku berarti kamu menyetujui versi yang baru.',
        },
      ],
    },
    {
      id: 'kontak',
      heading: 'Kontak',
      blocks: [
        {
          kind: 'p',
          text: `Pertanyaan tentang kebijakan ini atau tentang data pribadimu bisa dikirim ke ${CONTACT_EMAIL}. Kami berusaha menjawab dalam waktu wajar sejak permintaan diterima.`,
        },
      ],
    },
  ],
}

const EN: LegalDocument = {
  title: 'Privacy Policy',
  subtitle:
    'What we collect when you use YT Clip AI, why we keep it, and what you can do about it.',
  effectiveDate: EFFECTIVE_DATE,
  effectiveLabel: 'Effective from',
  summary:
    'In short: we store basic identity details from your Google account, your top-up records, and usage metadata (model, token counts, duration, cost). We do not store the contents of your prompts or the model responses, and we never receive your card details or payment PIN.',
  tocLabel: 'Contents',
  sections: [
    {
      id: 'ruang-lingkup',
      heading: 'Scope',
      blocks: [
        {
          kind: 'p',
          text: 'This policy covers the YT Clip AI website, its dashboard, the API we provide, and the desktop application that connects to the service. By using any of them, you agree to the handling described below.',
        },
        {
          kind: 'p',
          text: 'YT Clip AI is an AI credit service: you top up a balance and spend it calling AI models through our API. The service is operated from Indonesia and is governed by Indonesian law.',
        },
      ],
    },
    {
      id: 'data-yang-dikumpulkan',
      heading: 'Data we collect',
      blocks: [
        {
          kind: 'p',
          text: 'We collect only what the service needs in order to run and to be financially accountable:',
        },
        {
          kind: 'ul',
          items: [
            'Account data — the name, email address, and profile picture Google provides when you sign in. Google is the only way in; we never receive or store your Google password.',
            'Transaction data — top-up amounts in USD and IDR, the platform fee, the exchange rate applied, payment status, timestamps, and the payment provider reference. We never receive or store your card number, PIN, OTP, or mobile banking and e-wallet credentials — those go directly to the payment provider.',
            'Usage metadata — for every request to an AI model: the model name, input and output token counts, duration, success or failure status, the error message if any, and the amount charged.',
            'API key data — the name you gave the key, the spend limit you set, when it was created, and whether it is active or revoked.',
            'Technical data — IP address, browser type, and server logs recorded automatically when you reach the service, used for security and troubleshooting.',
          ],
        },
      ],
    },
    {
      id: 'prompt-dan-jawaban',
      heading: 'Prompt and response contents',
      blocks: [
        {
          kind: 'p',
          text: 'The usage records we keep are metadata only — model, tokens, duration, cost. The prompts you send and the responses the model returns are not stored in the YT Clip AI database.',
        },
        {
          kind: 'p',
          text: 'They are, however, forwarded to the model provider you selected, because that is the only way your request can be answered. What the provider does with that content is governed by their privacy policy, not this one. Do not send sensitive personal data, trade secrets, or credentials in a prompt.',
        },
      ],
    },
    {
      id: 'cara-kami-memakai',
      heading: 'How we use the data',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Running the service — creating your account, issuing API keys, forwarding your requests to the model, and charging your balance for what you used.',
            'Processing payments — generating the QRIS code, matching incoming payments, and crediting your balance.',
            'Showing your history — so you can audit every request and every top-up, and what each one cost.',
            'Security and abuse prevention — detecting misuse, suspicious activity, or breaches of the terms of service.',
            'Support — answering your questions and investigating problems you report.',
            'Legal obligations — meeting tax and accounting requirements and responding to lawful requests from competent authorities.',
          ],
        },
        {
          kind: 'p',
          text: 'We do not sell your personal data, and we do not use it for targeted advertising.',
        },
      ],
    },
    {
      id: 'pihak-ketiga',
      heading: 'Third parties that process data',
      blocks: [
        {
          kind: 'p',
          text: 'To operate the service, limited data is shared with the following providers:',
        },
        {
          kind: 'ul',
          items: [
            'Google — for authentication. Google gives us your name, email, and profile picture once you grant permission.',
            'The QRIS payment provider — to issue payment codes and confirm incoming payments. They receive the amount and the transaction reference.',
            'AI model providers — to process each request you send, including its prompt contents.',
            'Infrastructure and hosting providers — to run the servers, the database, and site delivery.',
            'Email and Telegram providers — for service notifications and, for Gold members, access to the VIP support group.',
          ],
        },
        {
          kind: 'p',
          text: 'Some of these providers are located outside Indonesia, so data may be processed across borders. We may also disclose data where required by law or to protect the rights and safety of other users.',
        },
      ],
    },
    {
      id: 'cookie',
      heading: 'Cookies and local storage',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Session cookie — a single httpOnly cookie that marks you as signed in. The dashboard cannot work without it, so it is strictly necessary rather than a tracking cookie.',
            'Browser local storage — holds display preferences such as the language you chose on this page. It is never sent to our servers.',
          ],
        },
        {
          kind: 'p',
          text: 'We set no advertising cookies and no third-party trackers for profiling.',
        },
      ],
    },
    {
      id: 'aplikasi-desktop',
      heading: 'Desktop app and device tokens',
      blocks: [
        {
          kind: 'p',
          text: 'The desktop app can be used without registering. On first launch it registers itself and receives one device token; all we hold is that device identity, its operating system platform, the app version, its balance, and its usage history — no name and no email.',
        },
        {
          kind: 'p',
          text: 'The token is shown exactly once and cannot be retrieved again. Because there is no account to recover, a lost token means a lost balance. Keep it safe and do not share it.',
        },
      ],
    },
    {
      id: 'keamanan',
      heading: 'Security',
      blocks: [
        {
          kind: 'ul',
          items: [
            'All traffic to the service runs over HTTPS.',
            'Sign-in sessions live in an httpOnly cookie, so scripts in the browser cannot read them.',
            'Integration credentials are stored encrypted and are never sent back to the browser.',
            'An API key secret is shown only once, at creation; after that we keep only a form that cannot be turned back into the original.',
          ],
        },
        {
          kind: 'p',
          text: 'No system is perfectly secure. If you suspect your account has been misused, revoke your API keys from the dashboard immediately and contact us.',
        },
      ],
    },
    {
      id: 'retensi',
      heading: 'How long we keep data',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Account data is kept for as long as your account is active.',
            'Transaction and usage records are kept for as long as bookkeeping, audit, and legal obligations require, even after an account is closed.',
            'Technical logs are kept short-term for security and troubleshooting.',
          ],
        },
      ],
    },
    {
      id: 'hak-kamu',
      heading: 'Your rights',
      blocks: [
        {
          kind: 'ul',
          items: [
            'View your account details, transaction history, and usage history from the dashboard at any time.',
            'Request correction of inaccurate data.',
            'Request deletion of your account. Financial records we are legally required to retain will be kept, and any remaining balance is forfeited under the Terms & Conditions.',
            'Withdraw consent by ceasing to use the service and revoking the application in your Google account settings.',
          ],
        },
        {
          kind: 'p',
          text: `Send such requests to ${CONTACT_EMAIL} from the email address registered on your account.`,
        },
      ],
    },
    {
      id: 'usia',
      heading: 'Age requirement',
      blocks: [
        {
          kind: 'p',
          text: 'The service is not intended for anyone under 18. If you are under 18, you may use it only with the consent and supervision of a parent or guardian. If we learn of an account created by a minor without that consent, we will delete it.',
        },
      ],
    },
    {
      id: 'perubahan',
      heading: 'Changes to this policy',
      blocks: [
        {
          kind: 'p',
          text: 'We may update this policy from time to time. The effective date at the top of this page always reflects the current version. For significant changes we will notify you by email or through a notice in the dashboard. Continuing to use the service after a change takes effect means you accept the new version.',
        },
      ],
    },
    {
      id: 'kontak',
      heading: 'Contact',
      blocks: [
        {
          kind: 'p',
          text: `Questions about this policy or about your personal data can be sent to ${CONTACT_EMAIL}. We aim to respond within a reasonable time of receiving a request.`,
        },
      ],
    },
  ],
}

export const PRIVACY_POLICY: Record<LegalLanguage, LegalDocument> = { id: ID, en: EN }
